// Extracted from src/container/ScribeTabs/TabPane/rich-text-editor-lazy.tsx so
// every lazy-chunk surface (rich text editor, artifact panels, linked-context
// session tabs) shares one retry + classification seam —
// history: git log --follow src/container/ScribeTabs/TabPane/rich-text-editor-lazy.tsx

import { datadogRum } from '@datadog/browser-rum';
import {
  Component,
  lazy,
  type ComponentType,
  type FunctionComponent,
  type JSX,
  type ReactNode,
} from 'react';

import { logger } from '@/utils/logger/logger';
import {
  reportBoundaryError,
  type LocalBoundaryName,
} from '@/utils/telemetry/boundary-error';

export type Importer<P> = () => Promise<{ default: FunctionComponent<P> }>;

function notifyObserver(observer: () => void): void {
  try {
    observer();
  } catch {
    // Load telemetry must never change whether the chunk module resolves.
  }
}

/**
 * `lazy` with a retry seam: React caches a rejected chunk import in the lazy
 * component forever, so a failed load would otherwise stay broken until a full
 * reload. The rejected instance is kept until `retryIfFailed()` swaps in a
 * fresh `lazy` (re-running the same import URL). This can recover a transient
 * fetch failure on the next mount of the guarded slot; a stale post-deploy
 * hash still requires a page refresh. Call `retryIfFailed()` from the guarding
 * boundary's mount path (e.g. its constructor) rather than inside the
 * suspended tree, where a persistent failure would import-loop.
 */
export function createRetryLazy<P extends object>(
  importer: Importer<P>,
  {
    onFailed,
    onLoaded,
    onRecovered,
  }: {
    onFailed?: (error: unknown) => void;
    onLoaded?: () => void;
    onRecovered?: () => void;
  } = {}
) {
  let failed = false;
  let everFailed = false;
  let Lazy = wrap();

  function wrap() {
    return lazy(async () => {
      let chunkModule: Awaited<ReturnType<Importer<P>>>;
      try {
        chunkModule = await importer();
      } catch (error) {
        failed = true;
        everFailed = true;
        notifyObserver(() => onFailed?.(error));
        throw error;
      }

      notifyObserver(() => onLoaded?.());
      if (everFailed) {
        everFailed = false;
        notifyObserver(() => onRecovered?.());
      }
      return chunkModule;
    });
  }

  function retryIfFailed() {
    if (failed) {
      failed = false;
      Lazy = wrap();
    }
  }

  function RetryLazy(props: P) {
    // TS can't reduce LazyExoticComponent's PropsWithRef<P> for an unresolved
    // generic; the lazy wraps a FunctionComponent<P>, so this signature is exact.
    const Resolved = Lazy as unknown as (componentProps: P) => JSX.Element;
    return <Resolved {...props} />;
  }

  return { RetryLazy, retryIfFailed };
}

/** Distinguishes a JS/CSS chunk-load failure from a component render throw. */
export function isChunkLoadError(error: unknown): error is Error {
  if (!(error instanceof Error)) {
    return false;
  }

  const code = (error as Error & { code?: unknown }).code;
  return (
    code === 'CSS_CHUNK_LOAD_FAILED' ||
    error.name === 'ChunkLoadError' ||
    /loading (?:css )?chunk .+ failed|dynamically imported module|importing a module script failed/i.test(
      error.message
    )
  );
}

/**
 * Builds a chunk-failure reporter that dedupes on error identity: the shared
 * importer observes a rejection first (via `createRetryLazy`'s `onFailed`), so
 * a mounted boundary re-reporting that same instance is a no-op, while a
 * chunk-shaped throw the importer never saw — an inner dynamic import, or a
 * module that loads then fails while evaluating — still gets reported once
 * instead of falling through the gap. Non-chunk errors are never reported
 * here; render throws are the boundary's own responsibility.
 */
export function createChunkFailureReporter({
  feature,
  logMessage,
}: {
  feature: string;
  logMessage: string;
}): (error: unknown) => void {
  const reportedChunkErrors = new WeakSet<Error>();

  return (error: unknown): void => {
    if (!isChunkLoadError(error) || reportedChunkErrors.has(error)) {
      return;
    }
    reportedChunkErrors.add(error);

    logger.error(logMessage, error);
    datadogRum.addError(error, { feature, phase: 'chunk_load' });
  };
}

/**
 * The boundary's `surface` prop is mandatory exactly when the chunk configures
 * a surface union, and unpassable otherwise — attribution can't be forgotten
 * on a multi-mount chunk, and a single-mount one can't emit a stray RUM facet.
 */
type ChunkBoundaryProps<S extends string> = { children: ReactNode } & ([S] extends [never]
  ? { surface?: never }
  : { surface: S });

/**
 * Pairs {@link createRetryLazy} with the error boundary that guards it — the
 * two are useless apart, and hand-rolling the boundary per surface duplicated
 * the retry-on-mount, chunk-vs-render classification and reporting three ways.
 *
 * Pass an `S` union only for a chunk mounted from more than one place (the
 * session tab strip backs both the Evidence linked-session preview and the
 * agent-chat artifact panel). Each mount must then name its `surface`, which
 * rides the RUM render context and `renderLogMessage` so a failure is
 * attributed to the caller that saw it.
 *
 * The returned `ChunkBoundary` retries from its constructor — the mount path,
 * not the suspended tree, where a persistent failure would import-loop.
 *
 * Every chunk emits `<actionPrefix>_chunk_loaded` and
 * `<actionPrefix>_chunk_retry_recovered` RUM actions: the denominator the
 * failure count needs, and the evidence that the retry seam earns its keep.
 */
export function createLazyChunk<P extends object, S extends string = never>({
  importer,
  feature,
  actionPrefix = feature,
  boundaryName = 'lazy_chunk',
  chunkLogMessage,
  renderLogMessage,
  Fallback,
  reportChunkFailure = createChunkFailureReporter({
    feature,
    logMessage: chunkLogMessage,
  }),
}: {
  importer: Importer<P>;
  feature: string;
  /**
   * Names the load-health actions, defaulting to `feature`. Override only to
   * keep an existing action name stable — RUM action names are the key
   * dashboards and monitors query on, so renaming one silently zeroes them.
   */
  actionPrefix?: string;
  /** Preserve an existing render-error facet when adopting this shared boundary. */
  boundaryName?: LocalBoundaryName;
  chunkLogMessage: string;
  renderLogMessage: (surface: S) => string;
  Fallback: ComponentType;
  /**
   * Pass a reporter built elsewhere when a second call site (e.g. a preload
   * thunk) also reports this chunk — the WeakSet dedupe is per reporter, so
   * separate instances would report the same rejection twice.
   */
  reportChunkFailure?: (error: unknown) => void;
}) {
  const { RetryLazy, retryIfFailed } = createRetryLazy<P>(importer, {
    onFailed: reportChunkFailure,
    // Derived rather than opt-in: the failure count is unreadable without the
    // load count as its denominator, and a per-surface opt-in is exactly what
    // both existing surfaces forgot to wire.
    onLoaded: () => datadogRum.addAction(`${actionPrefix}_chunk_loaded`),
    onRecovered: () => datadogRum.addAction(`${actionPrefix}_chunk_retry_recovered`),
  });

  class ChunkBoundary extends Component<
    { children: ReactNode; surface?: S },
    { hasError: boolean }
  > {
    state = { hasError: false };

    constructor(props: { children: ReactNode; surface?: S }) {
      super(props);
      retryIfFailed();
    }

    static getDerivedStateFromError() {
      return { hasError: true };
    }

    componentDidCatch(error: Error) {
      try {
        if (isChunkLoadError(error)) {
          // Deduped per error instance — a no-op for the rejection the importer
          // already reported; covers a chunk-shaped throw it never observed.
          reportChunkFailure(error);
          return;
        }
        const { surface } = this.props;
        logger.error(renderLogMessage(surface as S), error);
        reportBoundaryError(error, {
          boundary: boundaryName,
          fatal: false,
          context: { feature, phase: 'render', ...(surface ? { surface } : {}) },
        });
      } catch {
        // Reporting must never break the boundary itself — a throw here would
        // escape to the app root and blank the surface it exists to protect.
      }
    }

    render() {
      return this.state.hasError ? <Fallback /> : this.props.children;
    }
  }

  return {
    Lazy: RetryLazy,
    // The class is written against the permissive prop shape so `surface` can
    // be read internally; callers see the conditional one.
    ChunkBoundary: ChunkBoundary as ComponentType<ChunkBoundaryProps<S>>,
  };
}
